import { Phone, MapPin, Clock, ClipboardCheck, Wrench, ExternalLink } from "lucide-react";
import WhatsAppTelCompanion from "@/components/WhatsAppTelCompanion";

const MAPS_DIRECT_URL = "https://maps.app.goo.gl/U7LshpX1Z6bQxhDn8";
const MAPS_EMBED_URL =
  "https://www.google.com/maps?q=Selena+Service+Auto,+Strada+General+Magheru+73,+Slobozia,+Ialomita&z=16&output=embed";

export default function Contact({ t }) {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
          {t.contact.eyebrow}
        </div>
        <h2 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-4xl sm:text-5xl lg:text-7xl leading-[0.9] max-w-4xl">
          {t.contact.title}
        </h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#e2e6ec] border border-[#e2e6ec]">
          <div className="lg:col-span-5 bg-white p-8 lg:p-10 grid gap-8">
            <PhoneBlock testid="contact-service" icon={Wrench}
              label={t.contact.service} phone="+40 343 101 131" accent="#0b378c" />
            <PhoneBlock testid="contact-service-mobile" icon={Phone}
              label={t.contact.serviceMobile} phone="+40 753 094 642" accent="#e3191b" />
            <PhoneBlock testid="contact-itp" icon={ClipboardCheck}
              label={t.contact.itp} phone="+40 740 050 120" accent="#0b378c" />
          </div>

          <div className="lg:col-span-7 bg-[#eaf0fb] relative min-h-[480px] flex flex-col">
            {/* Real Google Maps iframe */}
            <div className="relative flex-1 min-h-[360px] bg-[#0b378c]/10">
              <iframe
                data-testid="google-map-iframe"
                src={MAPS_EMBED_URL}
                title="Selena Service — Slobozia, Ialomița"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>

            {/* Info block under map */}
            <div className="bg-white border-t border-[#e2e6ec] p-6 lg:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InfoBlock testid="contact-address" icon={MapPin}
                  label={t.contact.addressLabel} lines={[t.contact.address]} />
                <InfoBlock testid="contact-hours" icon={Clock}
                  label={t.contact.hoursLabel}
                  lines={[t.contact.hours, t.contact.closed]} />
              </div>
              <a
                data-testid="contact-open-maps"
                href={MAPS_DIRECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 bg-[#0b378c] hover:bg-[#082a6e] text-white font-bold uppercase tracking-wider text-xs px-4 py-2.5 transition-colors"
              >
                {t.contact.openInMaps}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {t.contact.mapNote}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneBlock({ testid, icon: Icon, label, phone, accent }) {
  const telHref = `tel:${phone.replace(/\s/g, "")}`;
  return (
    <div data-testid={testid}>
      <div
        className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em]"
        style={{ color: accent }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        {label}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <a
          href={telHref}
          className="inline-flex items-center gap-3 font-[Barlow_Condensed] font-bold text-4xl lg:text-5xl text-[#0b1220] hover:opacity-80 transition-opacity"
        >
          <Phone className="w-6 h-6 shrink-0" style={{ color: accent }} />
          {phone}
        </a>
        <WhatsAppTelCompanion
          telHref={telHref}
          trackingChannel="contact_phone_block"
          iconClassName="h-9 w-9 lg:h-10 lg:w-10"
        />
      </div>
    </div>
  );
}

function InfoBlock({ testid, icon: Icon, label, lines }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#0b378c]">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 text-sm text-[#0b1220] leading-relaxed">
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  );
}
